import React, { useState, useMemo } from 'react';
import { Grid, Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    LocationOn as LocationIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Business as BusinessIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    ToggleOn as ToggleOnIcon,
    ToggleOff as ToggleOffIcon,
    Edit as EditIcon,
    // Delete as DeleteIcon,
} from '@mui/icons-material';

const LocationDataGrid = ({
    activeTab,
    setActiveTab,
    filteredLocationList = [],
    sizePerPage,
    setSizePerPage,
    page,
    setPage,
    toggleDefault,
    toggleStatus,
    handleEditClick,
    toggleDelete,
}) => {

    // Filter locations by shipmentType
    const b2bLocations = useMemo(() => {
        return filteredLocationList.filter(location =>
            !location.shipmentType ||
            location.shipmentType === 'B2B' ||
            location.shipmentType === null ||
            location.shipmentType === undefined
        );
    }, [filteredLocationList]);

    const b2cLocations = useMemo(() => {
        return filteredLocationList.filter(location =>
            location.shipmentType === 'B2C'
        );
    }, [filteredLocationList]);

    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
        setPage(0); // Reset to first page when switching tabs
    };

    // Common columns configuration
    const getColumns = () => [
        {
            headerName: "Name",
            field: "name",
            flex: 1,
            headerAlign: "left",
            renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <BusinessIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Name</Typography>
                </Box>
            ),
            renderCell: ({ row }) => (
                <Grid container spacing={0.5}>
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.name}
                        </Typography>
                    </Grid>
                </Grid>
            ),
        },
        {
            headerName: "Address",
            field: "pincode",
            flex: 1.5,
            headerAlign: "left",
            renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Address</Typography>
                </Box>
            ),
            renderCell: ({ row }) => {
                const addArr = [];
                if (row.address_line) addArr.push(row.address_line);
                if (row.city) addArr.push(row.city);
                if (row.state) addArr.push(row.state);
                if (row.pincode) addArr.push(row.pincode);
                if (row.country) addArr.push(row.country);
                const address = addArr.join(", ");
                return (
                    <Tooltip title={address} arrow>
                        <Typography
                            variant="body2"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                                color: "text.secondary"
                            }}
                        >
                            {address || "-"}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            headerName: "Contact No",
            field: "contactNumber",
            flex: 1,
            headerAlign: "left",
            renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PhoneIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Contact No</Typography>
                </Box>
            ),
            renderCell: ({ row }) => (
                <Typography variant="body2">
                    {row.contactNumber || "-"}
                </Typography>
            ),
        },
        {
            headerName: "Email",
            field: "email",
            flex: 1,
            headerAlign: "left",
            renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <EmailIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Email</Typography>
                </Box>
            ),
            renderCell: ({ row }) => (
                <Grid container spacing={0.5}>
                    <Grid item xs={12}>
                        {row.email || "-"}
                    </Grid>
                </Grid>

            ),
        },
        {
            headerName: "GSTIN",
            field: "gstin",
            flex: 1,
            headerAlign: "left",
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {row.gstin || "-"}
                </Typography>
            ),
        },
        {
            headerName: "Status",
            field: "status",
            flex: 0.8,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                const isActive = row.status === "ACTIVE";
                return (
                    <Chip
                        icon={isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        label={row.status}
                        size="small"
                        color={isActive ? "success" : "error"}
                        sx={{
                            fontWeight: 600,
                            minWidth: 90,
                            "& .MuiChip-icon": {
                                fontSize: "1rem"
                            }
                        }}
                    />
                );
            },
        },
        {
            headerName: "Default",
            field: "is_default",
            flex: 0.8,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                return (
                    <Tooltip title={row.is_default ? "Default Location" : "Set as Default"}>
                        <IconButton
                            onClick={() => toggleDefault(row.id, row.is_default)}
                            size="small"
                            sx={{
                                color: row.is_default ? "warning.main" : "action.disabled",
                                "&:hover": {
                                    backgroundColor: row.is_default
                                        ? "warning.light"
                                        : "action.hover",
                                    color: row.is_default ? "warning.dark" : "warning.main",
                                },
                                transition: "all 0.2s ease-in-out"
                            }}
                        >
                            {row.is_default ? (
                                <StarIcon fontSize="medium" />
                            ) : (
                                <StarBorderIcon fontSize="medium" />
                            )}
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            headerName: "Actions",
            field: "action",
            flex: 1,
            headerAlign: "center",
            align: "center",
            sortable: false,
            renderCell: ({ row }) => {
                const isActive = row.status === "ACTIVE";
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title={isActive ? "Deactivate Location" : "Activate Location"}>
                            <IconButton
                                onClick={() => toggleStatus(row.id, row.status)}
                                size="small"
                                sx={{
                                    color: isActive ? "success.main" : "error.main",
                                    "&:hover": {
                                        backgroundColor: isActive
                                            ? "success.light"
                                            : "error.light",
                                        transform: "scale(1.1)",
                                    },
                                    transition: "all 0.2s ease-in-out"
                                }}
                            >
                                {isActive ? (
                                    <ToggleOnIcon fontSize="large" />
                                ) : (
                                    <ToggleOffIcon fontSize="large" />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Location">
                            <IconButton
                                onClick={() => handleEditClick(row.id)}
                                size="small"
                                sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                        backgroundColor: "primary.light",
                                        transform: "scale(1.1)",
                                    },
                                    transition: "all 0.2s ease-in-out"
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="Delete Location">
                            <IconButton
                                onClick={() => toggleDelete(row.id, row.is_deleted)}
                                size="small"
                                sx={{
                                    color: "error.main",
                                    "&:hover": {
                                        backgroundColor: "error.light",
                                        transform: "scale(1.1)",
                                    },
                                    transition: "all 0.2s ease-in-out"
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip> */}
                    </Box>
                );
            },
        },
    ];

    return (
        <TabContext value={activeTab}>
            <Box sx={{ backgroundColor: "#fff", boxShadow: 1, mt: 2 }}>
                <TabList
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    sx={{
                        "& .MuiTab-root": {
                            px: 4,
                            py: 1.5,
                            fontWeight: 500,
                        },
                    }}
                >
                    <Tab
                        label={`B2B Locations (${b2bLocations?.length})`}
                        value="B2B"
                    />
                    <Tab
                        label={`B2C Locations (${b2cLocations?.length})`}
                        value="B2C"
                    />
                </TabList>
            </Box>
            <TabPanel value="B2B" sx={{ px: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ProzoDataGrid
                            rows={b2bLocations}
                            getRowId={(row) => row?.name}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            rowCount={b2bLocations?.length || 0}
                            paginationMode={'client'}
                            columns={getColumns()}
                        />
                    </Grid>
                </Grid>
            </TabPanel>
            <TabPanel value="B2C" sx={{ px: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ProzoDataGrid
                            rows={b2cLocations}
                            getRowId={(row) => row?.name}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            rowCount={b2cLocations?.length || 0}
                            paginationMode={'client'}
                            columns={getColumns()}
                        />
                    </Grid>
                </Grid>
            </TabPanel>
        </TabContext>
    );
};

export default LocationDataGrid;
